import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { isIncomingImage } from "./IncomingFileUpload";

const positionAnchor = (viewport, imageElement, anchor) => {
  const bounds = viewport.getBoundingClientRect();
  const image = imageElement.getBoundingClientRect();
  viewport.scrollLeft += image.left - bounds.left - viewport.clientLeft + anchor.image.x * image.width - anchor.x;
  viewport.scrollTop += image.top - bounds.top - viewport.clientTop + anchor.image.y * image.height - anchor.y;
};

export default function IncomingFilePreview({ url, filename, type }) {
  const isImage = isIncomingImage(filename, type);
  const viewportRef = useRef(null);
  const imageRef = useRef(null);
  const pendingAnchorRef = useRef(null);
  const pointersRef = useRef(new Map());
  const gestureRef = useRef(null);
  const clickStartRef = useRef(null);
  const wheelHandlerRef = useRef(null);
  const [imageSize, setImageSize] = useState(null);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(null);
  const [dragging, setDragging] = useState(false);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    const measure = () => setViewportSize({ width: viewport.clientWidth, height: viewport.clientHeight });
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(viewport);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isImage]);

  const fitScale = imageSize && viewportSize.width && viewportSize.height
    ? Math.min(1, Math.max(1, viewportSize.width - 24) / imageSize.width, Math.max(1, viewportSize.height - 24) / imageSize.height)
    : 1;
  const scale = zoom === null ? fitScale : zoom / 100;
  const percent = Math.round(scale * 100);
  const minimumZoom = Math.min(10, fitScale * 100);
  const width = imageSize ? Math.round(imageSize.width * scale) : null;
  const height = imageSize ? Math.round(imageSize.height * scale) : null;

  const imagePointAt = (point) => {
    const bounds = imageRef.current.getBoundingClientRect();
    return { x: (point.x - bounds.left) / bounds.width, y: (point.y - bounds.top) / bounds.height };
  };
  const zoomTo = (value, point, imagePoint) => {
    if (!imageSize) return;
    const viewport = viewportRef.current;
    const bounds = viewport.getBoundingClientRect();
    const focus = point || { x: bounds.left + viewport.clientLeft + viewport.clientWidth / 2, y: bounds.top + viewport.clientTop + viewport.clientHeight / 2 };
    const anchor = { image: imagePoint || imagePointAt(focus), x: focus.x - bounds.left - viewport.clientLeft, y: focus.y - bounds.top - viewport.clientTop };
    const nextZoom = Math.max(minimumZoom, Math.min(400, value));
    if (Math.abs(nextZoom - scale * 100) < 0.001) {
      positionAnchor(viewport, imageRef.current, anchor);
      return;
    }
    pendingAnchorRef.current = anchor;
    setZoom(nextZoom);
  };
  useLayoutEffect(() => {
    if (pendingAnchorRef.current && imageRef.current) {
      positionAnchor(viewportRef.current, imageRef.current, pendingAnchorRef.current);
      pendingAnchorRef.current = null;
    }
  }, [zoom, width, height]);

  wheelHandlerRef.current = (event) => {
    if (!imageSize || !event.deltaY) return;
    event.preventDefault();
    clickStartRef.current = null;
    const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewportRef.current.clientHeight : 1);
    zoomTo(scale * 100 * Math.exp(-Math.max(-300, Math.min(300, delta)) * 0.002), { x: event.clientX, y: event.clientY });
  };
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    // A non-passive listener keeps wheel/pinch zoom inside this preview.
    const onWheel = event => wheelHandlerRef.current(event);
    viewport.addEventListener("wheel", onWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", onWheel);
  }, [isImage]);

  const pinch = (points) => ({
    center: { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 },
    distance: Math.max(1, Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y)),
  });
  const startGesture = () => {
    const points = Array.from(pointersRef.current.values());
    const viewport = viewportRef.current;
    if (points.length === 2) {
      const current = pinch(points);
      gestureRef.current = { ...current, zoom: scale * 100, image: imagePointAt(current.center) };
    } else {
      gestureRef.current = points.length ? { ...points[0], left: viewport.scrollLeft, top: viewport.scrollTop } : null;
    }
    setDragging(points.length > 0);
  };
  const onPointerDown = (event) => {
    if (!imageSize || event.button !== 0 || pointersRef.current.size >= 2) return;
    const viewport = viewportRef.current;
    const bounds = viewport.getBoundingClientRect();
    if (event.clientX >= bounds.left + viewport.clientLeft + viewport.clientWidth || event.clientY >= bounds.top + viewport.clientTop + viewport.clientHeight) return;
    event.preventDefault();
    viewport.focus({ preventScroll: true });
    viewport.setPointerCapture(event.pointerId);
    clickStartRef.current = !pointersRef.current.size && event.target === imageRef.current
      ? { id: event.pointerId, x: event.clientX, y: event.clientY }
      : null;
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    startGesture();
  };
  const onPointerMove = (event) => {
    if (!pointersRef.current.has(event.pointerId) || !gestureRef.current) return;
    const point = { x: event.clientX, y: event.clientY };
    const clickStart = clickStartRef.current;
    if (clickStart && Math.hypot(point.x - clickStart.x, point.y - clickStart.y) > 6) clickStartRef.current = null;
    pointersRef.current.set(event.pointerId, point);
    const gesture = gestureRef.current;
    if (pointersRef.current.size === 2) {
      const current = pinch(Array.from(pointersRef.current.values()));
      zoomTo(gesture.zoom * current.distance / gesture.distance, current.center, gesture.image);
    } else {
      viewportRef.current.scrollLeft = gesture.left - (point.x - gesture.x);
      viewportRef.current.scrollTop = gesture.top - (point.y - gesture.y);
    }
  };
  const endPointer = (event) => {
    if (!pointersRef.current.delete(event.pointerId)) return;
    const clickStart = clickStartRef.current;
    clickStartRef.current = null;
    // Let the browser release capture after pointerup/cancel so the final
    // click stays in the preview, even when a drag ends over the backdrop.
    startGesture();
    // Only a stationary click/tap zooms; dragging, pinching, and cancelled
    // gestures must not add a zoom when the pointer is released.
    if (event.type === "pointerup" && clickStart && clickStart.id === event.pointerId
      && Math.hypot(event.clientX - clickStart.x, event.clientY - clickStart.y) <= 6) {
      zoomTo(scale * 100 * 1.5, { x: event.clientX, y: event.clientY });
    }
  };
  const fitImage = () => {
    pendingAnchorRef.current = null;
    setZoom(null);
    if (viewportRef.current) {
      viewportRef.current.scrollLeft = 0;
      viewportRef.current.scrollTop = 0;
    }
  };

  if (isImage) return (
    <div className="incoming-image-preview">
      <div className="incoming-preview-toolbar" role="group" aria-label="Image zoom controls">
        <button type="button" className="btn btn-outline-secondary" aria-label="Zoom out" title="Zoom out"
          disabled={!imageSize || scale * 100 <= minimumZoom} onClick={() => zoomTo(scale * 100 / 1.25)}>−</button>
        <span className="incoming-preview-zoom" aria-live="polite">{percent}%</span>
        <button type="button" className="btn btn-outline-secondary" aria-label="Zoom in" title="Zoom in"
          disabled={!imageSize || scale * 100 >= 400} onClick={() => zoomTo(scale * 100 * 1.25)}>+</button>
        <button type="button" className="btn btn-outline-secondary" disabled={!imageSize} onClick={fitImage} title="Fit image to window">Fit</button>
      </div>
      <div className="incoming-preview-help">Click to zoom in · Drag to move · Scroll or pinch to zoom</div>
      <div ref={viewportRef} className={`incoming-preview-viewport${dragging ? " incoming-preview-dragging" : ""}`} role="region" aria-label="Image preview" tabIndex="0"
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endPointer} onPointerCancel={endPointer} onLostPointerCapture={endPointer}>
        <div className="incoming-preview-image-stage" style={imageSize ? { width: width + 24, height: height + 24 } : undefined}>
          <img ref={imageRef} src={url} alt="Full Preview" className="incoming-preview-image" draggable={false}
            style={imageSize ? { width, height } : { maxWidth: "100%" }}
            onLoad={(event) => setImageSize({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })} />
        </div>
      </div>
    </div>
  );

  if (type === "application/pdf" || /\.pdf$/i.test(filename)) return (
    <iframe src={url} title={`Preview of ${filename}`} className="incoming-preview-document" />
  );

  return (
    <div className="incoming-preview-download">
      <i className="fa fa-file-o" aria-hidden="true" />
      <p>{filename}</p>
      <a href={url} download={filename} className="btn btn-primary">Download file</a>
    </div>
  );
}
